import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import api from "../../services/api";
import { SocketContext, socketManager } from "../Socket/SocketContext";

export const ChangelogContext = createContext();

export const ChangelogProvider = ({ children }) => {
  const socketCtx = useContext(SocketContext);
  const [loading, setLoading] = useState(false);
  const [releases, setReleases] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [roadmap, setRoadmap] = useState([]);
  const [roadmapDirty, setRoadmapDirty] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/changelog/unread/count");
      setUnreadCount(data.count || 0);
    } catch (err) {
      // Silencioso
    }
  }, []);

  const fetchReleases = useCallback(async (reset = false) => {
    // Evita chamadas concorrentes
    if (loading) return;
    setLoading(true);
    try {
      const nextPageIndex = reset ? 0 : page; // page representa a próxima página a ser carregada
      const { data } = await api.get("/changelog", { params: { limit: 5, offset: nextPageIndex * 5 } });
      const batch = data.releases || [];

      // Define se ainda há mais dados (se retornou exatamente 5 assume potencial de próxima página)
      setHasMore(batch.length === 5);

      setReleases(prev => {
        if (reset) return batch;
        // Evita duplicados caso offset/carga chegue repetida por qualquer motivo de socket
        const existingIds = new Set(prev.map(r => r.id));
        const merged = [...prev];
        batch.forEach(r => { if (!existingIds.has(r.id)) merged.push(r); });
        return merged;
      });

      if (reset) {
        setPage(1); // próxima será página 1
      } else {
        setPage(p => p + 1); // avança
      }
    } catch (err) {
      setError("Erro ao carregar changelog");
    } finally {
      setLoading(false);
    }
  }, [loading, page]);

  // Funções incrementais para eventos de socket
  const upsertReleaseFromSocket = useCallback(async (payload) => {
    // Para simplicidade: quando release criada/publish/updated, buscamos apenas aquela release pública (se published)
    if (!payload.releaseId && !payload.id) return;
    const id = payload.releaseId || payload.id;
    try {
      // Buscar release individual somente se published; drafts não entram na lista pública
      const { data } = await api.get("/changelog", { params: { limit: 1, offset: 0 } });
      // Fallback simples: se não temos endpoint single public, fazemos refresh leve só da primeira página se for release nova
      // (Estratégia minimalista até endpoint /changelog/:id público existir)
      fetchReleases(true);
    } catch {
      fetchReleases(true);
    }
  }, [fetchReleases]);

  const removeRelease = useCallback((id) => {
    setReleases(prev => prev.filter(r => r.id !== id));
  }, []);

  // (Desativado approach anterior de "invalidar" itens localmente)
  const updateItemsForRelease = useCallback((payload) => {
    if (!payload.releaseId) return;
    // Estratégia revisada: refazer fetch da primeira página para garantir sincronização dos itens.
    // Como o volume inicial é pequeno (5 releases) o custo é baixo e mantém consistência.
    fetchReleases(true);
  }, [fetchReleases]);

  const fetchRoadmap = useCallback(async () => {
    try {
      const { data } = await api.get("/roadmap", { params: { light: 1 } });
      const list = data.itens || [];

      let changedIds = [];
      setRoadmap(prev => {
        if (!prev.length) return list;
        const mapPrev = new Map(prev.map(p => [p.id, p]));
        const merged = list.map(n => {
          const old = mapPrev.get(n.id);
          if (!old) return n; // novo item
          // Detecta mudança relevante comparando updatedAt
            const updatedChanged = old.updatedAt && n.updatedAt && old.updatedAt !== n.updatedAt;
          if (updatedChanged) {
            changedIds.push(n.id);
          }
          return {
            ...old,
            ...n,
            // Preserva descricaoMarkdown já carregada enquanto não re-hidratamos novamente
            descricaoMarkdown: old.descricaoMarkdown ?? n.descricaoMarkdown
          };
        });
        return merged;
      });

      // Se houve itens alterados (possível mudança de descrição), força hidratação completa
      if (changedIds.length > 0) {
        // pequena defasagem para garantir state atualizado
        setTimeout(() => {
          const fn = fetchRoadmapFullRef.current;
          if (typeof fn === "function") {
            fn(true);
          }
        }, 50);
      }
      // Não limpar roadmapDirty aqui: o indicador deve permanecer até o usuário abrir a aba Roadmap
    } catch { /* silencioso */ }
  }, []);

  // Hidratação completa (inclui descricaoMarkdown). Evita múltiplos hits: só executa se detectar ausência geral de descrições.
  // Ref para permitir chamada dentro de setTimeout sem depender de closures antigas
  const fetchRoadmapFullRef = React.useRef(null);

  const fetchRoadmapFull = useCallback(async (force = false) => {
    try {
      if (!force) {
        const missing = roadmap.some(r => !r.descricaoMarkdown);
        if (!missing) return; // Nada a hidratar
      }
      const { data } = await api.get("/roadmap");
      const full = data.itens || data || [];
      if (Array.isArray(full) && full.length > 0) {
        setRoadmap(prev => {
          const mapPrev = new Map(prev.map(p => [p.id, p]));
          return full.map(n => ({ ...mapPrev.get(n.id), ...n }));
        });
      }
    } catch { /* silencioso */ }
  }, [roadmap]);

  // Atualiza ref sempre que função muda
  useEffect(() => { fetchRoadmapFullRef.current = fetchRoadmapFull; }, [fetchRoadmapFull]);

  const markRead = useCallback(async (id) => {
    try {
      // Otimista: ajusta estado antes da resposta
      setReleases(prev => prev.map(r => r.id === id ? { ...r, naoLido: false, novo: false } : r));
      setUnreadCount(c => (c > 0 ? c - 1 : 0));
      await api.post(`/changelog/${id}/read`);
      // Depois confirma contagem real (caso múltiplas modificações simultâneas)
      fetchUnread();
    } catch {
      // Em caso de erro poderíamos refazer fetchReleases para consistência
    }
  }, [fetchUnread]);

  // Exposto para limpar a bolinha de roadmap quando o usuário abrir a aba
  const markRoadmapSeen = useCallback(() => {
    setRoadmapDirty(false);
  }, []);

  // Socket listeners
  useEffect(() => {
    // Fallback: se provider não existir, tenta obter socket direto do manager
    const sock = socketCtx?.currentSocket || socketCtx?.GetSocket?.() || socketManager.GetSocket?.();
    if (!sock) return;
    let roadmapDebounceTimer = null;
    let eventsDebounceTimer = null;
    const pendingEventsRef = { releases: false, unread: false, items: false };

    const debounceRoadmap = () => {
      if (roadmapDebounceTimer) clearTimeout(roadmapDebounceTimer);
      roadmapDebounceTimer = setTimeout(() => { fetchRoadmap(); }, 250);
    };

    const flushEvents = () => {
      const { releases: rel, unread, items } = pendingEventsRef;
      if (unread) fetchUnread();
      if (rel) fetchReleases(true);
      else if (items) fetchReleases(true); // items implicam refresh da primeira página
      pendingEventsRef.releases = false;
      pendingEventsRef.unread = false;
      pendingEventsRef.items = false;
    };

    const scheduleFlush = () => {
      if (eventsDebounceTimer) return; // já agendado
      eventsDebounceTimer = setTimeout(() => {
        eventsDebounceTimer = null;
        flushEvents();
      }, 160); // janela curta para agrupar rajadas
    };

    const handler = (payload) => {
      if (!payload || !payload.action) return;
      const action = payload.action;
      if (action === "release:created" || action === "release:published" || action === "release:updated") {
        pendingEventsRef.unread = true;
        pendingEventsRef.releases = true;
        scheduleFlush();
      } else if (action === "release:deleted") {
        removeRelease(payload.id || payload.releaseId);
        pendingEventsRef.unread = true;
        scheduleFlush();
      } else if (action === "release:read") {
        // Outro cliente marcou como lido. Se for o próprio usuário local, já otimizamos; mas garantimos consistência.
        const rid = payload.releaseId || payload.id;
        if (rid) {
          setReleases(prev => prev.map(r => r.id === rid ? { ...r, naoLido: false, novo: false } : r));
          // Recalcular contagem com leve debounce global (reaproveita mecanismo existente)
          pendingEventsRef.unread = true;
          scheduleFlush();
        }
      } else if (action === "release:unpublished") {
        const rid = payload.id || payload.releaseId;
        if (rid) removeRelease(rid);
        try { window.dispatchEvent(new CustomEvent('changelog-event', { detail: payload })); } catch {}
      } else if (action.startsWith("item:")) {
        pendingEventsRef.unread = true;
        pendingEventsRef.items = true;
        scheduleFlush();
      } else if (action.startsWith("roadmap:")) {
        setRoadmapDirty(true);
        debounceRoadmap();
      }
    };
    sock.on("changelog", handler);
    // Re-sync em reconexões
    const onConnect = () => {
      try { fetchUnread(); } catch {}
      try { fetchRoadmap(); } catch {}
    };
    try { sock.on("connect", onConnect); } catch {}
    return () => {
      try { sock.off("changelog", handler); } catch {}
      try { sock.off("connect", onConnect); } catch {}
      if (roadmapDebounceTimer) clearTimeout(roadmapDebounceTimer);
      if (eventsDebounceTimer) clearTimeout(eventsDebounceTimer);
    };
  }, [socketCtx, fetchUnread, fetchReleases, fetchRoadmap, removeRelease]);

  // Efeito inicial único: evita loop causado pela mudança de identidade de fetchReleases/page
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    didInitRef.current = true;
    fetchUnread();
    fetchReleases(true); // carrega somente as 5 mais recentes
    fetchRoadmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ChangelogContext.Provider value={{
      releases, unreadCount, roadmap, loading, error,
      fetchMore: () => fetchReleases(false), refresh: () => fetchReleases(true), markRead, hasMore,
      hydrateRoadmap: fetchRoadmapFull,
      roadmapDirty,
      markRoadmapSeen
    }}>
      {children}
    </ChangelogContext.Provider>
  );
};

export const useChangelog = () => {
  const ctx = useContext(ChangelogContext);
  if (!ctx) {
    return {
      releases: [],
      unreadCount: 0,
      roadmap: [],
      loading: false,
      error: null,
      fetchMore: () => {},
      refresh: () => {},
      markRead: () => {},
      hasMore: false,
      hydrateRoadmap: () => {},
      roadmapDirty: false,
      markRoadmapSeen: () => {}
    };
  }
  return ctx;
};
